import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SupportCategory, SupportStatus, SupportTicket } from '@reims-local/types';
import { supportApi } from '../lib/api';

const CATEGORY_LABEL: Record<SupportCategory, string> = {
  bug: '🐛 Bug',
  suggestion: '💡 Suggestion',
  question: '❓ Question',
  critical: '🚨 Critique',
};

type Row = { ticket: SupportTicket; shop: { id: string; name: string; slug: string; plan: string } };

export function Tickets() {
  const [rows, setRows] = useState<Row[]>([]);
  const [statusFilter, setStatusFilter] = useState<SupportStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<SupportCategory | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res = await supportApi.list({
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
      });
      setRows(res.data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, categoryFilter]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Tickets support ({rows.length})</h2>
        <div className="spacer" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as SupportStatus | '')}>
          <option value="">Tous statuts</option>
          <option value="new">Nouveau</option>
          <option value="in_progress">En cours</option>
          <option value="waiting">En attente</option>
          <option value="resolved">Résolu</option>
          <option value="closed">Fermé</option>
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value as SupportCategory | '')}>
          <option value="">Toutes catégories</option>
          <option value="critical">🚨 Critique</option>
          <option value="bug">🐛 Bug</option>
          <option value="question">❓ Question</option>
          <option value="suggestion">💡 Suggestion</option>
        </select>
        <button onClick={() => void load()}>Rafraîchir</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <p>Chargement…</p>
      ) : rows.length === 0 ? (
        <div className="card"><p>Aucun ticket.</p></div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Boutique</th>
              <th>Catégorie</th>
              <th>Sujet</th>
              <th>Statut</th>
              <th>SLA réponse</th>
              <th>Maj</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ ticket, shop }) => (
              <tr key={ticket.id}>
                <td><Link to={`/tickets/${ticket.id}`}>{ticket.ticket_number}</Link></td>
                <td>
                  <div>{shop.name}</div>
                  <div className="muted">{shop.plan}</div>
                </td>
                <td>{CATEGORY_LABEL[ticket.category as SupportCategory]}</td>
                <td>{ticket.subject}</td>
                <td><span className={`badge ${statusClass(ticket.status as SupportStatus)}`}>{statusLabel(ticket.status as SupportStatus)}</span></td>
                <td>
                  {ticket.first_response_at
                    ? <span className="badge green">Répondu</span>
                    : ticket.sla_response_due_at
                      ? <SlaBadge due={ticket.sla_response_due_at} />
                      : '—'}
                </td>
                <td className="muted">{fmt(ticket.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function TicketDetail({ ticketId }: { ticketId: string }) {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof supportApi.get>>['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const res = await supportApi.get(ticketId);
      setDetail(res.data);
      setAdminNote(res.data.ticket.admin_note ?? '');
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ticketId]);

  async function submitReply(e: React.FormEvent) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await supportApi.reply(ticketId, reply);
      setReply('');
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function changeStatus(status: SupportStatus) {
    await supportApi.patch(ticketId, { status });
    await load();
  }
  async function changeCategory(category: SupportCategory) {
    await supportApi.patch(ticketId, { category });
    await load();
  }
  async function saveNote() {
    await supportApi.patch(ticketId, { admin_note: adminNote });
    await load();
  }

  if (loading) return <p>Chargement…</p>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!detail) return null;
  const { ticket, shop, messages, merchant } = detail;

  return (
    <>
      <Link to="/tickets">← Retour aux tickets</Link>
      <h2 style={{ marginTop: 12 }}>{ticket.ticket_number} — {ticket.subject}</h2>

      <div className="card">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div><span className="muted">Catégorie :</span> {CATEGORY_LABEL[ticket.category as SupportCategory]}</div>
          <div><span className="muted">Statut :</span> <span className={`badge ${statusClass(ticket.status as SupportStatus)}`}>{statusLabel(ticket.status as SupportStatus)}</span></div>
          {shop && <div><span className="muted">Boutique :</span> <Link to={`/shops/${shop.id}`}>{shop.name}</Link> · {shop.plan}</div>}
          {merchant && <div><span className="muted">Contact :</span> {merchant.name ?? merchant.email}{merchant.phone && ` · ${merchant.phone}`}</div>}
        </div>
      </div>

      <div className="card">
        {messages.map((m) => (
          <div
            key={m.id}
            style={{
              marginBottom: 16,
              padding: 12,
              background: m.sender === 'admin' ? '#fff0f3' : '#f1f5f9',
              borderRadius: 8,
            }}
          >
            <div className="muted" style={{ fontSize: 12 }}>
              {m.sender === 'admin' ? 'Snitch Team (vous)' : 'Commerçant'} — {fmt(m.created_at)}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{m.content}</div>
          </div>
        ))}
      </div>

      {ticket.status !== 'closed' && (
        <form className="card" onSubmit={submitReply}>
          <h3 style={{ marginTop: 0 }}>Répondre</h3>
          <div className="form-row">
            <textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} />
          </div>
          <button type="submit" className="primary" disabled={sending}>
            {sending ? 'Envoi…' : 'Envoyer la réponse'}
          </button>
        </form>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Actions</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <button onClick={() => changeStatus('in_progress')} disabled={ticket.status === 'in_progress'}>→ En cours</button>
          <button onClick={() => changeStatus('waiting')} disabled={ticket.status === 'waiting'}>→ En attente</button>
          <button onClick={() => changeStatus('resolved')} className="primary" disabled={ticket.status === 'resolved'}>Marquer résolu</button>
          <button onClick={() => changeStatus('closed')} disabled={ticket.status === 'closed'}>Fermer</button>
        </div>
        <div className="form-row">
          <label>Changer la catégorie</label>
          <select value={ticket.category} onChange={(e) => changeCategory(e.target.value as SupportCategory)}>
            <option value="bug">🐛 Bug</option>
            <option value="suggestion">💡 Suggestion</option>
            <option value="question">❓ Question</option>
            <option value="critical">🚨 Critique</option>
          </select>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Note interne (non visible du commerçant)</h3>
        <textarea rows={4} value={adminNote} onChange={(e) => setAdminNote(e.target.value)} />
        <button onClick={saveNote} style={{ marginTop: 12 }}>Enregistrer la note</button>
      </div>
    </>
  );
}

function SlaBadge({ due }: { due: string }) {
  const msLeft = new Date(due).getTime() - Date.now();
  if (msLeft < 0) return <span className="badge red">SLA dépassé</span>;
  if (msLeft < 2 * 3600_000) return <span className="badge orange">&lt; 2h</span>;
  return <span className="badge">{Math.round(msLeft / 3600_000)}h</span>;
}
function statusClass(s: SupportStatus): string {
  if (s === 'resolved') return 'green';
  if (s === 'closed') return '';
  if (s === 'waiting') return 'blue';
  return 'orange';
}
function statusLabel(s: SupportStatus): string {
  const m: Record<SupportStatus, string> = {
    new: 'Nouveau', in_progress: 'En cours', waiting: 'En attente', resolved: 'Résolu', closed: 'Fermé',
  };
  return m[s];
}
function fmt(iso: string): string {
  try { return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)); } catch { return iso; }
}
